import { Link } from 'react-router-dom';
import {
  Truck,
  ShieldCheck,
  Clock,
  MapPin,
  ArrowRight,
  Trash2,
  Building2,
  Home as HomeIcon,
  Phone,
  Star,
  Sprout,
  TreePine,
  Tag,
  Package,
} from 'lucide-react';
import { COMPANY, weightLabelFromKg, campaignDiscountLabel } from '@/lib/data';
import CoverageChecker from '@/components/CoverageChecker';
import { useContainerPrices } from '@/lib/useContainerPrices';
import { useVat } from '@/lib/vat';

const FRACTION_CARDS = [
  {
    key: 'restavfall',
    title: 'Restavfall / blandet avfall',
    description: 'Rot, skrot og blandet avfall fra rydding og rehab.',
    icon: Trash2,
    link: '/bestill?fraksjon=restavfall',
  },
  {
    key: 'hageavfall',
    title: 'Hageavfall',
    description: 'Kvist, gress og hageavfall fra hagearbeid.',
    icon: Sprout,
    link: '/bestill?fraksjon=hageavfall',
  },
  {
    key: 'trevirke',
    title: 'Trevirke',
    description: 'Rent og malt trevirke fra riving og byggeprosjekt.',
    icon: TreePine,
    link: '/bestill?fraksjon=trevirke',
  },
  {
    key: 'storsekker',
    title: 'Bigbag – for små mengder',
    description: 'Perfekt når en container blir for stort. Til hage, oppussing og mindre rydding.',
    icon: Package,
    link: '/tjenester/storsekker',
  },
];

export default function HomePage() {
  const { activeCampaigns, hasActiveCampaigns } = useContainerPrices();
  const { formatPrice } = useVat();

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-bmg-ink overflow-hidden">
        <div className="absolute inset-0">
          <picture>
            <source media="(max-width: 767px)" srcSet="/hero-bmg-800.webp" width={800} height={600} />
            <img
              src="/hero-bmg-1920.webp"
              srcSet="/hero-bmg-800.webp 800w, /hero-bmg-1920.webp 1920w"
              sizes="100vw"
              alt="Gule containere fra Bergen Miljø og Gjenvinning"
              width={1920}
              height={1440}
              loading="eager"
              fetchPriority="high"
              className="h-full w-full object-cover object-[65%_50%] md:object-center opacity-30"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-bmg-ink via-bmg-ink/80 to-transparent" />
        </div>
        <div className="container-bmg relative py-20 lg:py-32">
          <div className="max-w-2xl">
            <div className="badge-bmg bg-bmg-yellow text-bmg-ink mb-4">
              <Star className="h-3.5 w-3.5" />
              Gaselle-bedrift 2024
            </div>
            <h1 className="text-bmg-white mb-4">
              Bestill container på 2 minutter. Betal med Vipps.
            </h1>
            <p className="text-lg text-bmg-white/80 mb-8 leading-relaxed">
              Bergen Miljø &amp; Gjenvinning leverer containere til privat, bedrift og
              borettslag i Bergensregionen. Lokalt eid, med utkjøring fra vår base i Øvre Ervik.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/bestill" className="btn-primary text-base">
                Bestill container nå
                <ArrowRight className="h-5 w-5" />
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`} className="btn-secondary text-base border-bmg-white/30 text-bmg-white hover:bg-bmg-white hover:text-bmg-ink">
                <Phone className="h-5 w-5" />
                {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Campaign section */}
      {hasActiveCampaigns && (
        <section className="bg-bmg-yellow-light border-b border-bmg-yellow">
          <div className="container-bmg py-10 lg:py-12">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 badge-bmg bg-bmg-yellow text-bmg-ink mb-3">
                <Tag className="h-3.5 w-3.5" /> Åpningstilbud
              </div>
              <h2 className="mb-2">Åpningstilbud ut året</h2>
              <p className="text-bmg-ink-soft max-w-2xl mx-auto">
                Vi feirer ny nettside med åpningstilbud på restavfall og trevirke. Gjelder bestillinger på nett til og med 31. desember 2026.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto mb-6">
              {activeCampaigns.map((c) => {
                const isRestavfall = c.fraction === 'restavfall';
                return (
                  <div key={`${c.fraction}-${c.size}`} className="card-bmg border-bmg-yellow bg-bmg-white flex flex-col">
                    <h3 className="text-lg font-bold mb-1">{c.fractionName} {c.size.replace(' kbm', ' m³')}</h3>
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-2xl font-bold text-bmg-yellow-text">{formatPrice(c.campaignPrice)}</span>
                      <span className="badge-bmg bg-bmg-success text-white">{campaignDiscountLabel(c.ordinaryPrice, c.campaignPrice)}</span>
                    </div>
                    <p className="text-sm text-bmg-ink-soft mb-2">Ordinær pris <span className="line-through">{formatPrice(c.ordinaryPrice)}</span></p>
                    <p className="text-sm text-bmg-ink-soft mb-4">{weightLabelFromKg(c.includedWeightKg)}</p>
                    <Link to={`/bestill?fraksjon=${c.fraction}`} className="btn-primary self-start">
                      {isRestavfall ? 'Bestill restavfall' : 'Bestill trevirke'} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                );
              })}
            </div>
            <div className="text-center">
              <Link to="/tilbud" className="text-sm font-bold text-bmg-ink hover:text-bmg-yellow-text inline-flex items-center gap-1">
                Se tilbudet <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Quick benefits bar */}
      <section className="border-b border-bmg-gray-border bg-bmg-gray">
        <div className="container-bmg py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-bmg-yellow-dark flex-shrink-0" />
              <div>
                <div className="text-sm font-bold">Bestill før kl. 15</div>
                <div className="text-xs text-bmg-ink-soft">Levering neste virkedag</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="h-6 w-6 text-bmg-yellow-dark flex-shrink-0" />
              <div>
                <div className="text-sm font-bold">Dekker Bergensregionen</div>
                <div className="text-xs text-bmg-ink-soft">Bergen, Os, Askøy, Arna</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-bmg-yellow-dark flex-shrink-0" />
              <div>
                <div className="text-sm font-bold">Faste priser</div>
                <div className="text-xs text-bmg-ink-soft">Inkl. mva, ingen overraskelser</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-bmg-yellow-dark flex-shrink-0" />
              <div>
                <div className="text-sm font-bold">Lokalt eid</div>
                <div className="text-xs text-bmg-ink-soft">Bergen Miljø &amp; Gjenvinning AS</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer types */}
      <section className="section-bmg">
        <div className="container-bmg">
          <h2 className="text-center mb-4">Privat eller bedrift?</h2>
          <p className="text-center text-bmg-ink-soft mb-12 max-w-2xl mx-auto">
            Vi har egne priser og bestillingsløsninger for privatkunder og for bedrifter og borettslag.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/bestill?kunde=privat" className="card-bmg group block">
              <HomeIcon className="h-10 w-10 text-bmg-yellow-dark mb-4" />
              <h3 className="mb-2">Privat</h3>
              <p className="text-bmg-ink-soft mb-4">
                Rydder du loftet, river et bad eller rydder i hagen? Bestill riktig container og betal med Vipps.
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-bmg-ink group-hover:text-bmg-yellow-text transition-colors">
                Bestill for privat <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
            <Link to="/bestill?kunde=bedrift" className="card-bmg group block">
              <Building2 className="h-10 w-10 text-bmg-yellow-dark mb-4" />
              <h3 className="mb-2">Bedrift &amp; borettslag</h3>
              <p className="text-bmg-ink-soft mb-4">
                Containerutleie og faste avtaler for bedrifter og borettslag. Faktura etter levering.
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-bmg-ink group-hover:text-bmg-yellow-text transition-colors">
                Bestill for bedrift <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Fraction chooser */}
      <section className="section-bmg bg-bmg-gray">
        <div className="container-bmg">
          <div className="text-center mb-10">
            <h2 className="mb-3">La oss finne riktig container til riktig oppdrag</h2>
            <p className="text-bmg-ink-soft max-w-2xl mx-auto">
              Velg hva du skal kaste, så viser vi deg størrelser og priser.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FRACTION_CARDS.map((card) => {
              const Icon = card.icon;
              return (
                <Link key={card.key} to={card.link} className="card-bmg group block">
                  <Icon className="h-10 w-10 text-bmg-yellow-dark mb-4" />
                  <h3 className="text-lg mb-2">{card.title}</h3>
                  <p className="text-sm text-bmg-ink-soft mb-5">{card.description}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-bmg-ink group-hover:text-bmg-yellow-text transition-colors">
                    Se priser og bestill <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <Link to="/fraksjoner" className="text-sm font-bold text-bmg-ink hover:text-bmg-yellow-text inline-flex items-center gap-1">
              Se alle fraksjoner <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section-bmg">
        <div className="container-bmg">
          <h2 className="text-center mb-12">Slik fungerer det</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-bmg-yellow text-bmg-ink font-bold text-xl mb-4">1</div>
              <h3 className="text-lg mb-2">Velg fraksjon</h3>
              <p className="text-sm text-bmg-ink-soft">Restavfall, trevirke, hageavfall eller annet. Vi viser pris rett i kortet.</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-bmg-yellow text-bmg-ink font-bold text-xl mb-4">2</div>
              <h3 className="text-lg mb-2">Velg størrelse</h3>
              <p className="text-sm text-bmg-ink-soft">Fra 10 til 30 kbm. Åpen eller lukket avhengig av omgivelsene.</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-bmg-yellow text-bmg-ink font-bold text-xl mb-4">3</div>
              <h3 className="text-lg mb-2">Adresse og dato</h3>
              <p className="text-sm text-bmg-ink-soft">Sjekk om adressen er i dekningsområdet. Bestill før kl. 15 for levering neste dag.</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-bmg-yellow text-bmg-ink font-bold text-xl mb-4">4</div>
              <h3 className="text-lg mb-2">Betal med Vipps eller faktura</h3>
              <p className="text-sm text-bmg-ink-soft">Ved betaling med Vipps reserveres beløpet når du bestiller, og trekkes når containeren leveres. Bedrifter betaler med faktura etter levering. Du får bekreftelse på e-post.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage area */}
      <section className="bg-bmg-gray py-8 lg:py-10">
        <div className="container-bmg">
          <div className="max-w-xl mx-auto text-center">
            <div className="flex items-center gap-2 text-bmg-yellow-text font-bold text-sm uppercase tracking-wide mb-1.5 justify-center">
              <Truck className="h-5 w-5" /> Dekningsområde
            </div>
            <h2 className="mb-1.5">Vi kjører i hele Bergensregionen</h2>
            <p className="text-bmg-ink-soft mb-5">
              Vi dekker hele Bergensregionen — skriv inn postnummeret ditt under, så sjekker vi om vi leverer akkurat til deg.
            </p>
            <CoverageChecker />
          </div>
        </div>
      </section>
    </div>
  );
}
